// src/components/leaderboard/LeaderboardMini.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  Stack,
  Tooltip,
  Button,          // ⬅️ para los botones Prev/Next
} from "@mui/material";

const SORT_OPTIONS = [
  { value: "ph",        label: "PH" },
  { value: "nft",       label: "NFTs" },
  { value: "burned",    label: "Burned" },
  { value: "saldo",     label: "Hueso" },
  { value: "fusion",    label: "Fusion" },
  { value: "real",      label: "Real $" },
  { value: "pe",        label: "PE" },
  { value: "porcentaje",label: "%" },
  { value: "nivel",     label: "Lvl" },
];

export function LeaderboardMini({
  limit = 8,
  defaultSort = "ph",
  listMaxHeight = 420, // altura por defecto (puedes pasar "100%" desde el padre)
  sx,
}) {
  const [sort, setSort] = useState(defaultSort);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // ▶️ PAGINACIÓN
  const [page, setPage] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [returned, setReturned] = useState(0);

  const endpoint = useMemo(
    () =>
      `/api/leaderboard?sort=${encodeURIComponent(sort)}&limit=${limit}&page=${page}`,
    [sort, limit, page]
  );

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setErr("");
    fetch(endpoint)
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (!cancel) {
          setRows(Array.isArray(j?.data) ? j.data : []);
          setTotalDocs(Number(j?.totalDocs || 0));
          setReturned(Number(j?.returned || 0));
        }
      })
      .catch((e) => !cancel && setErr(e.message || "Request error"))
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [endpoint]);

  const totalPages = Math.max(1, Math.ceil(totalDocs / limit));
  const startIdx = (page - 1) * limit + 1;
  const endIdx = Math.min(page * limit, totalDocs);

  const listSx = useMemo(
    () => ({
      maxHeight: listMaxHeight,
      height: listMaxHeight,
      overflowY: "auto",
    }),
    [listMaxHeight]
  );

  const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : (n ?? 0));

  // Controles Prev/Next
  const Pager = (
    <Stack direction="row" spacing={1} alignItems="center">
      <Button
        size="small"
        variant="outlined"
        onClick={() => setPage((p) => Math.max(1, p - 1))}
        disabled={page <= 1 || loading}
      >
        Prev
      </Button>
      <Typography variant="caption" sx={{ opacity: 0.9 }}>
        {startIdx}-{endIdx} / {fmt(totalDocs)} · Page {page}/{totalPages}
      </Typography>
      <Button
        size="small"
        variant="outlined"
        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        disabled={page >= totalPages || loading}
      >
        Next
      </Button>
    </Stack>
  );

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        bgcolor: "rgba(0,0,0,0.35)",
        border: "1px solid rgba(255,255,255,0.12)",
        color: "white",
        ...sx,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Leaderboard
        </Typography>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <FormControl size="small" variant="outlined" sx={{ minWidth: 120 }}>
            <InputLabel id="lb-sort" sx={{ color: "white" }}>Sort</InputLabel>
            <Select
              labelId="lb-sort"
              label="Sort"
              value={sort}
              onChange={(e) => { setSort(e.target.value); setPage(1); }} // reset page
              sx={{
                color: "white",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.3)" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.5)" },
                "& .MuiSvgIcon-root": { color: "white" },
              }}
            >
              {SORT_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {Pager}
        </Stack>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 1 }} />}
      {err && (
        <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>
          {err}
        </Typography>
      )}

      <List dense disablePadding sx={listSx}>
        {rows.map((u, idx) => {
          const left = (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={(startIdx - 1) + idx + 1} // ranking absoluto
                size="small"
                sx={{
                  height: 20,
                  "& .MuiChip-label": { px: 0.6, fontSize: 12, fontWeight: 700 },
                  bgcolor: "rgba(255,255,255,0.12)",
                  color: "white",
                }}
              />
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{u.nombre}</Typography>
            </Stack>
          );

          const right = (
            <Stack
              direction="row"
              spacing={0.8}
              alignItems="center"
              flexWrap="wrap"
              justifyContent="flex-end"
            >
              <Tooltip title="POWER HUESO">
                <Chip size="small" label={`🔋🦴PH ${fmt(u.ph)}`} />
              </Tooltip>
              <Tooltip title="NFTs (General + Special)">
                <Chip size="small" label={`🎴NFT ${fmt(u.nftTotal)}`} />
              </Tooltip>
              <Tooltip title="NFTs Burned">
                <Chip size="small" label={`🔥🎴 ${fmt(u.nftBurned)}`} />
              </Tooltip>
              <Tooltip title="Hueso balance">
                <Chip size="small" label={`🦴 ${fmt(u.dinero)}`} />
              </Tooltip>
              <Tooltip title="Real HUESO $">
                <Chip size="small" label={`🦴💵 ${fmt(u.dineroReal)}`} />
              </Tooltip>
              <Tooltip title="VIPHueso">
                <Chip size="small" label={`🍖 ${fmt(u.fusion)}`} />
              </Tooltip>
              <Tooltip title="POWER ENERGIA">
                <Chip size="small" label={`🔋⚡ ${fmt(u.pe)}`} />
              </Tooltip>
              <Tooltip title="POWER COLLECTION">
                <Chip size="small" label={`🔋🎯 ${fmt(u.porcentaje)}`} />
              </Tooltip>
              <Tooltip title="NIVEL">
                <Chip size="small" label={`🏅 ${fmt(u.nivel)}`} />
              </Tooltip>
            </Stack>
          );

          return (
            <ListItem
              key={`${u.nombre}-${(startIdx - 1) + idx + 1}`}
              divider
              sx={{
                py: 0.6,
                "& .MuiListItemText-primary": { color: "white" },
                borderColor: "rgba(255,255,255,0.08)",
              }}
              secondaryAction={right}
            >
              <ListItemText primary={left} />
            </ListItem>
          );
        })}

        {!loading && rows.length === 0 && (
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            No data
          </Typography>
        )}
      </List>

      {/* Controles al pie (opcional, mismos que arriba) */}
      <Stack direction="row" justifyContent="flex-end" mt={1}>
        {Pager}
      </Stack>
    </Box>
  );
}
